/** 28.09 (владелец: «админ зала может вписать в каждую игру то, как рассчитались игроки»;
 *  «подставляется в отчёт»): суммы по формам оплаты + сдача. Сервер переносит их в основной
 *  отчёт оператора (там они закрыты, как задаток); после принятия отчёта ОО — только через ОО.
 *  Подсказка «к оплате» — та же цена, что считает форма отчёта (calc-price по игрокам брони). */
import { useEffect, useState } from "react";
import { SheetDialog, NumPadPop } from "../../components/shared";
import { api } from "../../api/client";
import { useAuth } from "../../auth";
import type { HallGame, HallPay } from "../../hooks/useHallDay";
import h from "./hallAdmin.module.css";

const FIELDS = [["cash", "Нал"], ["terminal", "Терминал"], ["transfer", "Перевод"], ["invoice", "Р/С"], ["other", "Другое"], ["change_amount", "Сдача"]] as const;
type F = typeof FIELDS[number][0];

export function HallPaySheet({ g, payment, readOnly, onSave, onClose }: {
  g: HallGame; payment: HallPay | undefined; readOnly?: boolean; onSave: (body: Record<string, unknown>) => Promise<string | null>; onClose: () => void;
}) {
  const { user } = useAuth();
  const [v, setV] = useState<Record<F, number>>(() => Object.fromEntries(FIELDS.map(([k]) => [k, payment?.[k] ?? 0])) as Record<F, number>);
  const [src, setSrc] = useState(payment?.change_source || "safe");
  const [pad, setPad] = useState<{ k: F; el: HTMLElement } | null>(null);
  const [calc, setCalc] = useState<{ expected_total?: number; prepaid?: number } | null>(null);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api<{ expected_total?: number; prepaid?: number }>(`/api/game-reports/calc-price?booking_id=${g.booking_id}&players=${g.players || 0}`).then(setCalc).catch(() => setCalc(null));
  }, [g.booking_id, g.players]);
  const prepaid = calc?.prepaid ?? g.prepaid_amount ?? 0;
  const sum = v.cash + v.terminal + v.transfer + v.invoice + v.other + prepaid - v.change_amount;
  const fmt = (n: number) => n.toLocaleString("ru");
  return (
    <SheetDialog open onDismiss={onClose} ariaLabel="Расчёт игры" dragHandle maxHeight="85dvh">
      <div className={h.sheet}>
        <div className={h.sheetTitle}>Расчёт · {g.quest} {g.time}</div>
        <div className={h.payGrid}>
          {FIELDS.map(([k, label]) => (
            <button key={k} type="button" className={h.payCell} disabled={readOnly} onClick={(e) => setPad({ k, el: e.currentTarget })}>
              <span className={h.lbl}>{label}</span><b>{fmt(v[k])}</b>
            </button>
          ))}
          <div className={`${h.payCell} ${h.payRo}`} title="Задаток — из брони (ДК)"><span className={h.lbl}>Задаток</span><b>{fmt(prepaid)}</b></div>
        </div>
        {v.change_amount > 0 ? (
          <div className={h.srcRow}>
            <span className={h.lbl}>Сдачу дал</span>
            {[["safe", "Сейф админа"], ["operator", "Из своих"], ["oo", "ОО"]].map(([id, label]) => (
              <button key={id} type="button" className={`${h.chip} ${src === id ? h.chipOk : ""}`} onClick={() => setSrc(id!)}>{label}</button>
            ))}
          </div>
        ) : null}
        <div className={h.paySum}>
          Итого <b>{fmt(sum)}</b>{calc?.expected_total ? <span className={h.note}> · к оплате по брони {fmt(calc.expected_total)}</span> : null}
        </div>
        {payment ? <div className={h.note}>{payment.entered_by_name ? `Сейчас вписано: ${payment.entered_by_name}` : "Сейчас — суммы из отчёта оператора"}</div> : null}
        {err ? <div className={h.warn}>{err}</div> : null}
        {readOnly ? <div className={h.note}>Смена прошла — расчёт правит только ОО.</div> : null}
        <div className={h.confirmActs}>
          {readOnly ? null : <button type="button" className={h.accept} disabled={busy} onClick={() => {
            setBusy(true); setErr(null);
            void onSave({ ...v, change_source: v.change_amount ? src : "", change_source_id: src === "operator" ? user?.staffId ?? "" : "" })
              .then((e) => { setBusy(false); if (e) setErr(e); else onClose(); });
          }}>{busy ? "…" : "Сохранить расчёт"}</button>}
          <button type="button" className={h.ghost} onClick={onClose}>{readOnly ? "Закрыть" : "Отмена"}</button>
        </div>
        <div className={h.note}>Суммы уйдут в отчёт оператора по этой игре — там их поправит только ОО.</div>
      </div>
      <NumPadPop open={!!pad} anchor={pad?.el ?? null} label={FIELDS.find(([k]) => k === pad?.k)?.[1]} mode="money" value={pad ? v[pad.k] : 0} max={1_000_000}
        onCommit={(n) => { if (pad) setV((o) => ({ ...o, [pad.k]: Math.round(n) })); }} onClose={() => setPad(null)} />
    </SheetDialog>
  );
}
